import { sum } from "lodash";
import { useMemo } from "react";
import { Order } from "@/entities/order";
import {
  LuUsers,
  LuBoxes,
  LuHistory,
  LuSettings,
  LuFileClock,
  LuPackagePlus,
  LuShoppingCart,
  LuBriefcaseBusiness,
  LuPackageOpen,
} from "react-icons/lu";

import { DestinationButtonProps } from "./types";
import { useAbilities } from "@app/access-control";
import { preordersToAlertsCount } from "@shared/lib/misc";
import { AppAbility } from "@app/access-control/abilities";
import { usePendingOrdersQuery } from "@features/order/get";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";

type DestinationsFilterFn = (destination: DestinationButtonProps) => boolean;

const ADMIN_ROUTE_PREFIX = "/admin";

const collectSuperuserDestination: DestinationsFilterFn = (destination) =>
  destination.path.startsWith(ADMIN_ROUTE_PREFIX);

const collectAllRegularDestination: DestinationsFilterFn = (destination) =>
  !destination.shouldHide && !destination.path.startsWith(ADMIN_ROUTE_PREFIX);

const getCollectDestinationsForCurrentUserFn =
  (ability: AppAbility): DestinationsFilterFn =>
  (destination) => {
    if (
      destination.shouldHide ||
      destination.path.startsWith(ADMIN_ROUTE_PREFIX)
    )
      return false;

    if (!destination.requiredAbility) return true;

    const [action, subject] = destination.requiredAbility;

    return ability.can(action, subject);
  };

export const useDestinations = () => {
  const ability = useAbilities();
  const {
    user,
    activeCompany,
    isCurrentCompanyManager,
    isSuperuserActingBehalfOfCompany,
  } = useAuthenticationContext();
  const { data: pendingPreorders } = usePendingOrdersQuery({
    filters: { pageSize: 100, page: 1 },
  });

  const alerts = useMemo(
    () =>
      preordersToAlertsCount(
        (pendingPreorders?.rows || []) as unknown as Order[],
      ),
    [pendingPreorders?.rows],
  );

  const activeCompanyOrderAlerts = activeCompany
    ? alerts?.[activeCompany.serviceId!]
    : void 0;

  const totalOrderAlerts = sum(Object.values(alerts ?? {}));

  const destinations = useMemo<DestinationButtonProps[]>(
    () => [
      {
        id: "pending-orders",
        name: "Pending Orders",
        icon: LuFileClock,
        path: "/account/pending-orders",
        notificationText: isCurrentCompanyManager ? totalOrderAlerts : "",
        requiredAbility: ["navigate", "my-orders-page"],
      },
      {
        id: "orders",
        name:
          activeCompanyOrderAlerts &&
          (isSuperuserActingBehalfOfCompany || isCurrentCompanyManager)
            ? `${activeCompanyOrderAlerts} orders need approval!`
            : "Orders",
        icon: LuShoppingCart,
        path: "/account/orders",
        colorPalette:
          activeCompanyOrderAlerts && isCurrentCompanyManager ? "red" : void 0,
        notificationText: activeCompanyOrderAlerts?.toString() || "",
        requiredAbility: ["navigate", "my-orders-page"],
      },
      {
        id: "products",
        name: "Stock Management",
        icon: LuBoxes,
        path: "/account/stock-management",
        requiredAbility: ["navigate", "my-orders-page"],
      },
      {
        id: "receivings",
        name: "Receivings",
        icon: LuPackageOpen,
        path: "/account/receivings",
        shouldHide: !activeCompany?.enableAdvancedStock,
      },
      {
        id: "detailed-order",
        name: "Detailed Ordering",
        icon: LuPackagePlus,
        path: "/account/detailed-ordering",
        requiredAbility: ["navigate", "detailed-ordering-page"],
      },
      {
        id: "staff",
        name: "Staff",
        icon: LuUsers,
        path: "/company/staff",
        requiredAbility: ["navigate", "staff-page"],
      },
      {
        id: "settings",
        name: "Settings",
        icon: LuSettings,
        path: "/account/settings",
      },
      {
        id: "users-admin",
        name: "Users ",
        icon: LuUsers,
        path: `${ADMIN_ROUTE_PREFIX}/users`,
        colorPalette: "purple",
      },
      {
        id: "companies",
        name: "Companies",
        icon: LuBriefcaseBusiness,
        path: `${ADMIN_ROUTE_PREFIX}/companies`,
        colorPalette: "purple",
      },
      {
        id: "event-log",
        name: "Activity logs",
        icon: LuHistory,
        path:
          user?.isSuperuser && !isSuperuserActingBehalfOfCompany
            ? "/admin/event-log"
            : "/event-log",
        colorPalette:
          user?.isSuperuser && !isSuperuserActingBehalfOfCompany
            ? "purple"
            : undefined,
      },
    ],
    [
      activeCompanyOrderAlerts,
      totalOrderAlerts,
      alerts,
      ability,
      user?.companies,
      user?.isSuperuser,
      isCurrentCompanyManager,
      isSuperuserActingBehalfOfCompany,
      activeCompany?.enableAdvancedStock,
    ],
  );

  const availableDestinations = useMemo<DestinationButtonProps[]>(() => {
    if (user?.isSuperuser)
      return isSuperuserActingBehalfOfCompany
        ? destinations.filter(collectAllRegularDestination)
        : destinations.filter(collectSuperuserDestination);

    return destinations.filter(getCollectDestinationsForCurrentUserFn(ability));
  }, [
    ability,
    destinations,
    user?.isSuperuser,
    isSuperuserActingBehalfOfCompany,
  ]);

  return availableDestinations;
};
